import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { translations, LINKS, getNimnyAppUrl } from '../../data/translations';

const StoriesSection = () => {
  const { language, t } = useLanguage();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Track button clicks for analytics
  const trackClick = (buttonName) => {
    if (window.plausible) {
      window.plausible('Click', { props: { button: buttonName } });
    }
  };

  return (
    <section
      id="stories"
      ref={sectionRef}
      className="relative py-12 md:py-20 overflow-hidden"
    >
      {/* Transparent - stars show through */}

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6">
        <div className={`transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="overflow-hidden rounded-3xl border border-amber-200/15 bg-[#f5ebe0]/[0.06] p-6 backdrop-blur-sm shadow-2xl shadow-black/20 md:p-10">
            {/* Section header */}
            <div className="mb-8 text-center md:mb-10">
              <h2 className="font-sans text-2xl text-[#f5ebe0]/85 md:text-4xl">
                {t(translations.stories.readOnlineTitle)}
              </h2>
              <p className="mx-auto mt-3 max-w-md font-serif text-sm text-amber-200/45 md:text-base">
                {t(translations.stories.readOnlineDescription)}
              </p>
            </div>

            <div className="grid items-center gap-7 md:grid-cols-[minmax(250px,0.95fr)_1.05fr] md:gap-10">
              <div className="mx-auto w-full max-w-[440px] overflow-hidden rounded-xl border border-amber-200/15 shadow-xl shadow-black/30">
                <img
                  src={translations.stories.onlineCollectionCover}
                  alt={t(translations.stories.onlineCollectionAlt)}
                  className="block w-full h-auto"
                  loading="lazy"
                  width="550"
                  height="682"
                  decoding="async"
                />
              </div>

              <div className="text-center md:text-left">
                <h3 className="font-sans text-xl text-amber-200/85 md:text-2xl">
                  {t(translations.stories.onlineCollectionTitle)}
                </h3>
                <p className="mt-4 font-serif text-sm leading-relaxed text-amber-200/60 md:text-base">
                  {t(translations.stories.onlineCollectionBody)}
                </p>
                <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center md:justify-start">
                  <a
                    href={getNimnyAppUrl(language)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick('Read Online - Open Nimny App')}
                    className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#c9a87c] px-6 py-3 font-sans text-sm font-medium text-[#171421] transition-colors hover:bg-[#d4b896] sm:flex-1"
                    data-testid="read-online-app-link"
                  >
                    {t(translations.stories.openOnlineCollection)}
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                  <a
                    href={LINKS.amazon}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick('Four Weeks - Read on Amazon')}
                    className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl border border-[#c9a87c]/55 px-6 py-3 font-sans text-sm font-medium text-[#e1c7a3] transition-colors hover:border-[#c9a87c] hover:bg-[#c9a87c]/10 sm:flex-1"
                    data-testid="four-weeks-amazon-link"
                  >
                    {t(translations.stories.readOnAmazonTitle)}
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoriesSection;
